import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  ASSET3D_LABEL,
  getProjectMeta,
  setAsset3dState,
  type Asset3dState,
  type ProjectMeta,
} from "@/lib/prometheusProjectMeta";
import { requestExportApproval } from "@/lib/prometheusApprovals";
import {
  clearProjectAsset3d,
  getProjectAsset3d,
  saveProjectAsset3d,
} from "@/lib/prometheusAsset3dStore";
import { ArtifactGlbViewer } from "@/components/prometheus/os/ArtifactGlbViewer";

const STATES: Asset3dState[] = ["didactico", "importado", "aprobado"];

export type ArtifactAsset3dStateProps = {
  projectId: string;
  projectTitle: string;
  discipline?: string;
  className?: string;
};

export function ArtifactAsset3dState({
  projectId,
  projectTitle,
  discipline,
  className,
}: ArtifactAsset3dStateProps) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [meta, setMeta] = useState<ProjectMeta>(() =>
    getProjectMeta(projectId, projectTitle, discipline)
  );
  const [note, setNote] = useState(meta.asset3d.note || "");
  const [viewerRev, setViewerRev] = useState(0);
  const [hasFile, setHasFile] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    const m = getProjectMeta(projectId, projectTitle, discipline);
    setMeta(m);
    setNote(m.asset3d.note || "");
  }, [projectId, projectTitle, discipline]);

  const refreshAssetFlag = useCallback(async () => {
    try {
      const a = await getProjectAsset3d(projectId);
      setHasFile(!!a);
    } catch {
      setHasFile(false);
    }
  }, [projectId]);

  useEffect(() => {
    reload();
    void refreshAssetFlag();
    const sync = () => {
      reload();
      void refreshAssetFlag();
      setViewerRev((n) => n + 1);
    };
    window.addEventListener("prometheus-project-meta-changed", sync);
    window.addEventListener("prometheus-asset3d-changed", sync);
    return () => {
      window.removeEventListener("prometheus-project-meta-changed", sync);
      window.removeEventListener("prometheus-asset3d-changed", sync);
    };
  }, [reload, refreshAssetFlag]);

  const applyState = async (state: Asset3dState) => {
    if (state === "aprobado") {
      const ok = await requestExportApproval({
        filename: "asset-3d-aprobado",
        detail:
          "Marcar como APROBADO significa listo a prototipo/impresión. Confirma que cotas, material e interferencias fueron revisadas.",
      });
      if (!ok) {
        toast.message("Aprobación cancelada");
        return;
      }
    }
    setMeta(
      setAsset3dState(projectId, state, {
        note: note.trim() || undefined,
        title: projectTitle,
        discipline,
        source:
          state === "importado"
            ? meta.asset3d.source || (hasFile ? "IndexedDB import" : "CAD externo")
            : meta.asset3d.source,
      })
    );
    toast.success(`Asset 3D → ${ASSET3D_LABEL[state]}`);
  };

  const onPickFile = async (file: File | null) => {
    if (!file) return;
    setBusy(true);
    try {
      const saved = await saveProjectAsset3d(projectId, file);
      setMeta(
        setAsset3dState(projectId, "importado", {
          note: note.trim() || saved.fileName,
          source: `file:${saved.fileName}`,
          title: projectTitle,
          discipline,
        })
      );
      setHasFile(true);
      setViewerRev((n) => n + 1);
      toast.success(`Importado: ${saved.fileName}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo importar");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onClear = async () => {
    setBusy(true);
    try {
      await clearProjectAsset3d(projectId);
      setHasFile(false);
      setViewerRev((n) => n + 1);
      setMeta(
        setAsset3dState(projectId, "didactico", {
          note: note.trim() || undefined,
          source: undefined,
          title: projectTitle,
          discipline,
        })
      );
      toast.message("Modelo 3D eliminado del Artifact");
    } catch {
      toast.error("No se pudo borrar el asset");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={cn(
        "rounded-xl border border-sky-500/30 bg-sky-500/5 p-3 space-y-3 font-mono-tech",
        className
      )}
    >
      <div className="flex items-start gap-2">
        <Box className="w-3.5 h-3.5 text-sky-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-[10px] uppercase tracking-[0.14em] text-sky-400 font-bold">
            Asset 3D · import real
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
            GLB / GLTF / STL en IndexedDB (local). Import gold → didáctico SVG abajo si no hay
            archivo.
          </p>
        </div>
      </div>

      <ArtifactGlbViewer projectId={projectId} rev={viewerRev} />

      <div className="flex flex-wrap gap-1.5">
        <input
          ref={fileRef}
          type="file"
          accept=".glb,.gltf,.stl,model/gltf-binary,model/gltf+json"
          className="hidden"
          onChange={(e) => void onPickFile(e.target.files?.[0] ?? null)}
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-sky-500/40 bg-sky-500/15 text-sky-300 px-2.5 py-1.5 text-[11px] font-bold cursor-pointer hover:bg-sky-500/25 disabled:opacity-50"
        >
          <Upload className="w-3 h-3" />
          Importar modelo
        </button>
        {hasFile ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void onClear()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border/60 text-muted-foreground px-2.5 py-1.5 text-[11px] cursor-pointer hover:text-red-300 hover:border-red-500/40 disabled:opacity-50"
          >
            <Trash2 className="w-3 h-3" />
            Quitar
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-1">
        {STATES.map((s) => {
          const active = meta.asset3d.state === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => void applyState(s)}
              className={cn(
                "rounded-lg border px-2.5 py-1.5 text-[11px] font-bold cursor-pointer transition-colors",
                active
                  ? s === "aprobado"
                    ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"
                    : s === "importado"
                      ? "border-sky-500/50 bg-sky-500/15 text-sky-300"
                      : "border-amber-500/50 bg-amber-500/15 text-amber-200"
                  : "border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/30"
              )}
            >
              {ASSET3D_LABEL[s]}
            </button>
          );
        })}
      </div>

      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => {
          if (note.trim() !== (meta.asset3d.note || "")) {
            setMeta(
              setAsset3dState(projectId, meta.asset3d.state, {
                note: note.trim(),
                title: projectTitle,
                discipline,
              })
            );
          }
        }}
        placeholder="Nota: cotas, archivo, modelo AI…"
        className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-[12px] text-foreground placeholder:text-muted-foreground/45 focus:outline-none focus:border-sky-500/40"
      />
    </div>
  );
}
